import { Script, graphql } from "gatsby"
import React from "react"
import { Layout } from "@bw/layouts"
import { Seo, Section, CmsImage } from "@bw/bits"
import { Content, Header } from "../components/modules/mapModules.jsx"
import { Teasers, SearchPharmacy } from "@bw/modules"
import { Teaser, Breadcrumbs } from "@bw/partials"
import { useTranslation } from "react-i18next"
import shuffle from "../utils/shuffle"

export const Head = ({ data }) => (
  <>
    {data.collectionFormations.head && (
      <Script
        id={`user-script-${data.collectionFormations.id}`}
        dangerouslySetInnerHTML={{ __html: data.collectionFormations.head }}
      />
    )}
  </>
)

const FormationTemplate = ({ data, pageContext, location }) => {
  const { collectionFormations, formations, pharmacies, mount } = data
  const formation = collectionFormations
  const allFormations = shuffle(formations.nodes)
  const { t } = useTranslation()
  const breadcrumbsLinks = [
    {
      title: "Accueil",
      to: "/",
    },
    {
      title: formation.title,
      to: formation.slug,
    },
  ]

  if (mount) {
    breadcrumbsLinks.splice(1, 0, {
      title: mount.title,
      to: mount.slug,
    })
  }

  /**
   * Find pharmacies that offer this formation
   */
  let participating = []
  if (pharmacies?.nodes) {
    participating = pharmacies.nodes
      .filter(pharmacy => {
        return pharmacy.formations.map(p => p.id).includes(pageContext.id)
      })
      .map(p => ({
        ...p,
        state: p.state?.value,
        slug: p.slug + "/formation/",
      }))
  }

  return (
    <Layout color={formation.color?.value} {...{ pageContext }}>
      <Seo
        url={location.href}
        title={formation.metaTitle || formation.title}
        description={formation.metaDescription}
        image={formation.image?.url}
        meta={[
          {
            name: "robots",
            content: formation.doNotIndex
              ? "noindex, nofollow"
              : "max-image-preview:large, index, follow",
          },
        ]}
      />
      <Header modules={formation.header} page={formation} />
      {breadcrumbsLinks && <Breadcrumbs breadcrumbs={breadcrumbsLinks} />}
      <Content modules={formation.content} />
      {participating && participating.length > 0 && (
        <Section
          margin="100px 0"
          title={t("participatingPharmacies")}
          subtitle={t("to_participate_to_formation_please_contact_a_pharmarcy")}
        >
          <SearchPharmacy
            pharmacies={participating}
            map={false}
            textSearch={false}
          />
        </Section>
      )}
      {allFormations && allFormations.length > 0 && (
        <Section margin="100px 0" title={t("other_formations")}>
          <Teasers>
            {allFormations.map((item, i) => {
              return (
                <Teaser
                  smaller
                  key={i}
                  title={item.title}
                  excerpt={item.teaserExcerpt}
                  to={item.slug || item.uri}
                  image={
                    item.teaserImage ? (
                      <CmsImage src={item.teaserImage} alt={item.title} />
                    ) : null
                  }
                />
              )
            })}
          </Teasers>
        </Section>
      )}
    </Layout>
  )
}

export default FormationTemplate

export const formationQuery = graphql`
  query formationQuery($id: String!) {
    collectionFormations(id: { eq: $id }) {
      id
      head
      slug
      title
      pictogram {
        value
      }
      metaDescription
      image {
        url
      }
      metaTitle
      doNotIndex
      color {
        value
      }
      teaserExcerpt
      teaserImage {
        localImage {
          childImageSharp {
            gatsbyImageData(width: 254, height: 139)
          }
        }
      }
      header {
        __typename
        suptitle
        title
        subtitle

        ... on PageHeaderModule {
          ...PageHeaderModuleFragment
        }
        ... on HeroModule {
          ...HeroModuleFragment
        }
      }
      content {
        __typename
        suptitle
        title
        subtitle
        buttonType {
          value
        }
        buttonUrl
        buttonIsExternal
        buttonText
        buttonPosition {
          value
        }
        buttonEntry {
          url
        }
        ancre

        ...CallToActionModuleFragment
        ...ContentModuleFragment
        ...ContentWithImageModuleFragment
        ...GalleryModuleFragment
        ...ContentOnColumnsModuleFragment
        ...TeasersLatestModuleFragment
        ...LogoGridModuleFragment
        ...ImageModuleFragment
        ...VideoModuleFragment
        ...DownloadsModuleFragment
      }
    }
    pharmacies: allCollectionPharmacies {
      nodes {
        id
        formations {
          id
        }
        title
        slug
        street
        zipCode
        city
        state {
          value
        }
        latitude
        longitude
      }
    }
    formations: allCollectionFormations(filter: { id: { ne: $id } }) {
      nodes {
        title
        teaserExcerpt
        slug
        teaserImage {
          localImage {
            childImageSharp {
              gatsbyImageData(width: 351, height: 175)
            }
          }
        }
      }
    }
    mount: collectionPages(mount: { handle: { eq: "formations" } }) {
      slug
      title
      header {
        ... on PageHeaderModule {
          image {
            url
          }
        }
      }
    }
  }
`
